// Sudoku entry point. Constructs every singleton, wires the menu router +
// game scene, and runs the launch-plan boot order: i18n → settings → URL
// share-link → saved active session → MainMenu. Share-link wins over resume;
// resume wins over starting at MainMenu.

import '@bn-games/ui-shell/styles.css';
import './styles.css';
import {
  MenuRouter,
  announce,
  createMainMenuScreen,
  setupMenuScale,
} from '@bn-games/ui-shell';
import { BoardRenderer } from './render/BoardRenderer.js';
import { NumpadRenderer } from './render/NumpadRenderer.js';
import { StatusBarRenderer } from './render/StatusBarRenderer.js';
import { HintController } from './hints/HintController.js';
import { LayoutManager } from './layout/LayoutManager.js';
import { SettingsManager } from './settings/SettingsManager.js';
import { GameScene } from './scenes/GameScene.js';
import { GameRecorder } from './history/GameRecorder.js';
import { HistoryStore } from './history/HistoryStore.js';
import { buildMenuContext } from './scenes/MenuContextImpl.js';
import {
  startNewGame as bootStartNewGame,
  startSharedPuzzle,
  resumeActiveGame as bootResumeActiveGame,
  type BootstrapDeps,
} from './scenes/Bootstrap.js';
import { routeFromUrl } from './scenes/routeFromUrl.js';
import { openShareModal } from './ui/ShareModal.js';
import { encodeShareUrl } from './share/encode.js';
import { createPlayMenuScreen } from './ui/screens/PlayMenu.js';
import { createSettingsScreen } from './ui/screens/Settings.js';
import { createRulesScreen } from './ui/screens/Rules.js';
import { createAboutScreen } from './ui/screens/About.js';
import { createHistoryListScreen } from './ui/screens/HistoryList.js';
import { initI18n, setLocale, t } from './i18n/I18n.js';
import { registerServiceWorker } from './pwa.js';
import type { Layout } from './layout/types.js';
import type { Scene } from './ui/types.js';
import type { Settings } from './settings/types.js';

function setupCanvas(): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.tabIndex = 0;
  canvas.style.display = 'none';
  canvas.setAttribute('role', 'application');
  canvas.setAttribute('aria-label', t('game.a11y.canvasLabel'));
  document.body.appendChild(canvas);
  return canvas;
}

// Match the canvas backing-store and CSS size to the layout snapshot. Renderers
// work in CSS pixels; the DPR-scaled transform handles physical-pixel mapping.
function applyCanvasLayout(canvas: HTMLCanvasElement, layout: Layout): void {
  const { width: cssW, height: cssH } = layout.viewport;
  const dpr = layout.devicePixelRatio;
  const bmW = Math.max(1, Math.round(cssW * dpr));
  const bmH = Math.max(1, Math.round(cssH * dpr));
  if (canvas.width !== bmW) canvas.width = bmW;
  if (canvas.height !== bmH) canvas.height = bmH;
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('Failed to acquire 2D canvas context');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  document.body.setAttribute('data-layout', layout.mode);
  document.body.setAttribute('data-orientation', layout.orientation);
}

function setCanvasVisible(canvas: HTMLCanvasElement, visible: boolean): void {
  canvas.style.display = visible ? 'block' : 'none';
  document.body.setAttribute('data-mode', visible ? 'game' : 'menu');
}

async function main(): Promise<void> {
  const settingsManager = new SettingsManager();
  await initI18n(settingsManager.get().language);
  registerServiceWorker();
  setupMenuScale();
  document.body.setAttribute('data-theme', settingsManager.get().theme);

  const canvas = setupCanvas();
  const layoutManager = new LayoutManager();
  applyCanvasLayout(canvas, layoutManager.getConfig());
  layoutManager.onChange((cfg) => {
    applyCanvasLayout(canvas, cfg);
    gameScene.markDirty();
  });

  const historyStore = new HistoryStore();
  const gameRecorder = new GameRecorder();
  const boardRenderer = new BoardRenderer();
  const numpadRenderer = new NumpadRenderer();
  const statusBarRenderer = new StatusBarRenderer();
  const hintController = new HintController();

  const menuRoot = document.getElementById('menu-root');
  if (menuRoot === null) throw new Error('#menu-root not found');

  // Sentinel — `navigateTo` short-circuits same-scene no-ops, so we start in
  // a value that no real route uses; the first explicit navigateTo() lands.
  let currentScene: Scene | null = null;
  const gameScene = new GameScene({
    canvas,
    layoutManager,
    settingsManager,
    boardRenderer,
    numpadRenderer,
    statusBarRenderer,
    hintController,
    gameRecorder,
    historyStore,
    onExit: () => navigateTo('menu'),
    onShare: (state) => {
      openShareModal({
        url: encodeShareUrl({
          seed: state.puzzle.seed,
          difficulty: state.puzzle.difficulty,
        }),
      });
    },
    onRestart: () => bootStartNewGame(bootstrap, settingsManager.get().difficulty),
  });

  const bootstrap: BootstrapDeps = { gameScene };

  function onSettingsChanged(before: Settings, after: Settings): void {
    if (after.theme !== before.theme) {
      document.body.setAttribute('data-theme', after.theme);
    }
    if (after.language !== before.language) {
      void setLocale(after.language).then(() => {
        router.remountCurrent();
        canvas.setAttribute('aria-label', t('game.a11y.canvasLabel'));
      });
    }
  }

  const ctxMenu = buildMenuContext({
    bootstrap,
    gameRecorder,
    historyStore,
    settingsManager,
    navigate: (scene) => navigateTo(scene),
    onSettingsChanged,
  });

  const router = new MenuRouter(menuRoot, ctxMenu);
  router.register('menu', () =>
    createMainMenuScreen({
      version: __APP_VERSION__,
      onQuickStart: () => ctxMenu.startGame(settingsManager.get().difficulty),
    }),
  );
  router.register('play', createPlayMenuScreen);
  router.register('settings', createSettingsScreen);
  router.register('rules', createRulesScreen);
  router.register('about', createAboutScreen);
  router.register('history', createHistoryListScreen);

  function navigateTo(scene: Scene): void {
    if (scene === currentScene) return;
    const leavingGame = scene !== 'game' && currentScene === 'game';
    currentScene = scene;
    if (leavingGame) gameScene.unmount();
    if (scene === 'game') {
      router.hide();
      setCanvasVisible(canvas, true);
      return;
    }
    setCanvasVisible(canvas, false);
    router.show(scene);
  }

  // ── Boot order: share-link → resume → menu ─────────────────────────────
  const route = routeFromUrl(window.location.search);
  if (route.kind === 'shared') {
    // Land on the menu first so a "keep" choice in the discard modal has
    // somewhere to fall back to. Discard confirmation is plan-mandated when
    // the user already has a puzzle in progress.
    navigateTo('menu');
    ctxMenu.discardActiveSession(() => {
      navigateTo('game');
      startSharedPuzzle(bootstrap, {
        seed: route.decoded.seed,
        difficulty: route.decoded.difficulty,
        from: route.decoded.from,
        timeMs: route.decoded.timeMs,
      });
    });
    return;
  }
  if (route.kind === 'shareError') {
    announceShareError(route.reason);
  }
  if (ctxMenu.getActiveSession() !== null) {
    navigateTo('game');
    bootResumeActiveGame(bootstrap);
    return;
  }
  navigateTo('menu');
}

function announceShareError(reason: 'invalid' | 'outdated'): void {
  const message = reason === 'outdated'
    ? t('error.shareLink.outdated')
    : t('error.shareLink.invalid');
  announce(message, { assertive: true });
}

void main();
